import { useMemo } from "react";
import { Link } from "react-router-dom";

import type { DashboardLayoutView } from "@/lib/api/dashboardLayout";
import { EmptyState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { can } from "@/stores/auth-store";
import { DashboardHeader } from "../sections";
import { DashboardGrid } from "./DashboardGrid";
import { resolveWidgets } from "./resolve";

/** The current month as "YYYY-MM" for the greeting line. The default
 * dashboard reads it from the month summary; a customized one may not show
 * that summary at all, so it is taken from the clock rather than fetched. */
function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * The dashboard once a company has saved a layout: the same header as the
 * default dashboard, then only the widgets they chose, in their order and
 * sizes. A widget the viewer cannot see (module off, no permission) is simply
 * left out - it is never fetched - so one saved layout serves every role.
 */
export function CustomDashboard({ view }: { view: DashboardLayoutView }) {
  const widgets = useMemo(() => resolveWidgets(view.layout?.items ?? [], view.widgets), [view]);
  const canCustomize = can("settings.write");
  return (
    <div>
      <DashboardHeader
        period={currentPeriod()}
        extraActions={
          canCustomize ? (
            <Link
              to="/settings/dashboard"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors duration-200 ease-out hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <Icon name="sliders" className="h-3.5 w-3.5" />
              Customize
            </Link>
          ) : undefined
        }
      />
      {widgets.length === 0 ? (
        <EmptyState
          icon={<Icon name="layers" className="h-7 w-7" />}
          title="Nothing to show on this dashboard yet"
          description={canCustomize ? "Every widget is hidden or unavailable for you. Choose widgets in Dashboard Settings." : "None of the dashboard's widgets are available to you."}
          action={
            canCustomize ? (
              <Link to="/settings/dashboard" className="text-sm font-medium text-primary-700 underline hover:no-underline">
                Open Dashboard Settings
              </Link>
            ) : undefined
          }
        />
      ) : (
        <DashboardGrid widgets={widgets} />
      )}
    </div>
  );
}
